/**
 * PropertyServerLogs — first-party access log lens for one property.
 * Bot crawls, 404 patterns, 5xx bursts, top URLs by real visitor traffic.
 *
 * The Auriel-unique data layer — no third-party SEO tool has access to
 * the customer's actual server logs.
 */
import { useParams } from "react-router-dom";
import { Server, AlertTriangle, FileWarning, Bot, Activity } from "lucide-react";
import {
  useProperty, useCapabilitySample, useStartServerLogsPull,
} from "@/lib/queries";
import { VendorLens, DataTable, Headline } from "@/components/VendorLens";


export default function PropertyServerLogs() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const pull = useStartServerLogsPull(propertyId);

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <VendorLens
      vendorIcon={<Server className="h-5 w-5 text-primary" />}
      vendorTitle="Server logs"
      vendorDescription={
        <>
          First-party access-log analysis for <span className="font-mono">{p.domain}</span>.
          Auriel SSHes to your server with the per-property key, reads the
          recent access log, surfaces real visitor traffic + bot crawls +
          broken-page hits. The data no third-party SEO tool has.
          {!p.ssh_host && (
            <div className="mt-1 text-amber-700 dark:text-amber-400">
              <strong>Server SSH not connected.</strong> Set it up from the
              property card to enable this lens.
            </div>
          )}
        </>
      }
      pullCostNote="Free (just SSH command time). Takes ~5-15s for 4 reads."
      pullMutation={pull}
      tabs={[
        { key: "overview",   label: "Overview", body: <OverviewTab propertyId={propertyId} /> },
        { key: "top_urls",   label: "Top URLs (real traffic)", body: <TopUrlsTab propertyId={propertyId} /> },
        { key: "404s",       label: "404 patterns",            body: <FourOhFoursTab propertyId={propertyId} /> },
        { key: "5xx",        label: "5xx errors",              body: <FiveXxTab propertyId={propertyId} /> },
        { key: "bots",       label: "Bot crawls",              body: <BotsTab propertyId={propertyId} /> },
        { key: "googlebot",  label: "Googlebot URLs",          body: <GooglebotTab propertyId={propertyId} /> },
      ]}
    />
  );
}


function OverviewTab({ propertyId }: { propertyId: number }) {
  const top = useCapabilitySample("ssh_logs", "access_log_top_urls", { propertyId });
  const fourohfours = useCapabilitySample("ssh_logs", "access_log_404s", { propertyId });
  const fivexx = useCapabilitySample("ssh_logs", "access_log_5xx", { propertyId });
  const bots = useCapabilitySample("ssh_logs", "access_log_bot_crawls", { propertyId });

  const topRows = ((top.data?.response as unknown) as Array<Record<string, unknown>> | undefined) ?? [];
  const ohRows = ((fourohfours.data?.response as unknown) as Array<Record<string, unknown>> | undefined) ?? [];
  const xxRows = ((fivexx.data?.response as unknown) as Array<Record<string, unknown>> | undefined) ?? [];
  const botData = (bots.data?.response as Record<string, unknown> | undefined);
  const totals = (botData?.totals as Record<string, number> | undefined) ?? {};
  const linesScanned = botData?.lines_scanned as number | undefined;

  const totalHits = topRows.reduce((s, r) => s + Number(r.hits ?? 0), 0);
  const total404 = ohRows.reduce((s, r) => s + Number(r.hit_count ?? 0), 0);
  const total5xx = xxRows.reduce((s, r) => s + Number(r.hits ?? 0), 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="Total visitor hits"  value={totalHits}     sub={`top ${topRows.length} URLs`} />
        <Headline label="Total 404 hits"      value={total404}      sub={`across ${ohRows.length} URLs`} />
        <Headline label="Total 5xx hits"      value={total5xx}      sub={`across ${xxRows.length} URLs`} />
        <Headline label="Lines scanned"       value={linesScanned}  sub="last log window" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="Googlebot hits"   value={totals.googlebot ?? 0} />
        <Headline label="Bingbot hits"     value={totals.bingbot ?? 0} />
        <Headline label="AhrefsBot hits"   value={totals.ahrefsbot ?? 0} />
        <Headline label="SemrushBot hits"  value={totals.semrushbot ?? 0} />
      </div>
    </div>
  );
}


function TopUrlsTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ssh_logs", "access_log_top_urls", { propertyId });
  const rows = ((sample.data?.response as unknown) as Array<Record<string, unknown>> | undefined) ?? [];
  return (
    <DataTable
      title="Top URLs by real visitor hits"
      subtitle="2xx requests only, sorted by hit count. Cross-reference with GSC/Ahrefs to find pages with traffic but no organic visibility (or vice versa)."
      sample={sample.data} rowCount={rows.length}
      icon={<Activity className="h-5 w-5 text-primary" />}
      columns={[
        { key: "url", label: "URL", className: "font-mono text-[11px]" },
        { key: "hits", label: "Hits", align: "right",
          render: (r) => Number(r.hits ?? 0).toLocaleString() },
      ]}
      rows={rows}
    />
  );
}


function FourOhFoursTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ssh_logs", "access_log_404s", { propertyId });
  const rows = ((sample.data?.response as unknown) as Array<Record<string, unknown>> | undefined) ?? [];
  return (
    <DataTable
      title="404 patterns from access log"
      subtitle="URLs returning 404 with significant hit counts. Each is a candidate for a 301 redirect. Cross-reference with Ahrefs broken-backlinks for paid-link-to-404 detection."
      sample={sample.data} rowCount={rows.length}
      icon={<FileWarning className="h-5 w-5 text-amber-700 dark:text-amber-400" />}
      columns={[
        { key: "url", label: "URL", className: "font-mono text-[11px]" },
        { key: "hit_count", label: "404 hits", align: "right",
          render: (r) => Number(r.hit_count ?? 0).toLocaleString() },
      ]}
      rows={rows}
    />
  );
}


function FiveXxTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ssh_logs", "access_log_5xx", { propertyId });
  const rows = ((sample.data?.response as unknown) as Array<Record<string, unknown>> | undefined) ?? [];
  return (
    <DataTable
      title="5xx server errors"
      subtitle="URLs returning 5xx — bots seeing 5xx will deindex affected URLs. Critical alert."
      sample={sample.data} rowCount={rows.length}
      icon={<AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />}
      columns={[
        { key: "url", label: "URL", className: "font-mono text-[11px]" },
        { key: "status", label: "Status", align: "right" },
        { key: "hits", label: "Hits", align: "right",
          render: (r) => Number(r.hits ?? 0).toLocaleString() },
      ]}
      rows={rows}
    />
  );
}


function BotsTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ssh_logs", "access_log_bot_crawls", { propertyId });
  const data = sample.data?.response as Record<string, unknown> | undefined;
  const totals = (data?.totals as Record<string, number> | undefined) ?? {};
  const linesScanned = data?.lines_scanned as number | undefined;

  if (!sample.data?.available) {
    return (
      <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
        No bot-crawl data yet. Click <strong>Refresh</strong> at the top.
      </div>
    );
  }

  const botRows = Object.entries(totals)
    .map(([name, hits]) => ({ name, hits }))
    .sort((a, b) => b.hits - a.hits);
  const total = botRows.reduce((s, r) => s + r.hits, 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Headline label="Bot hits in window" value={total} sub={`of ${linesScanned?.toLocaleString() ?? "?"} lines scanned`} />
        <Headline label="Bot share" value={linesScanned && linesScanned > 0
          ? `${(total / linesScanned * 100).toFixed(1)}%`
          : "—"} />
      </div>
      <DataTable
        title="Bot hit counts"
        subtitle="Per-crawler hits. AhrefsBot/SemrushBot/DotBot dominating Googlebot = crawl-budget bleeding to parasites."
        sample={sample.data} rowCount={botRows.length}
        icon={<Bot className="h-5 w-5 text-primary" />}
        columns={[
          { key: "name", label: "Bot" },
          { key: "hits", label: "Hits", align: "right",
            render: (r) => Number(r.hits ?? 0).toLocaleString() },
          { key: "share", label: "Share", align: "right",
            render: (r) => total > 0 ? `${(Number(r.hits) / total * 100).toFixed(1)}%` : "—" },
        ]}
        rows={botRows as Array<Record<string, unknown>>}
      />
    </div>
  );
}


function GooglebotTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ssh_logs", "access_log_bot_crawls", { propertyId });
  const data = sample.data?.response as Record<string, unknown> | undefined;
  const rows = ((data?.googlebot_top_urls as Array<Record<string, unknown>> | undefined)) ?? [];
  return (
    <DataTable
      title="Top URLs Googlebot crawled"
      subtitle="Confirms Googlebot is actually visiting specific pages. Pages NOT here but in your sitemap = orphan-from-Google's-perspective."
      sample={sample.data} rowCount={rows.length}
      icon={<Bot className="h-5 w-5 text-primary" />}
      columns={[
        { key: "url", label: "URL", className: "font-mono text-[11px]" },
        { key: "hits", label: "Crawls", align: "right",
          render: (r) => Number(r.hits ?? 0).toLocaleString() },
      ]}
      rows={rows}
    />
  );
}
